import { getSsrContext, runWithSsrContext } from './ssrRequestContext';

/** unstable_cache hits are near-instant; above this → treat wrapper path as miss (already logged). */
const CACHE_HIT_MAX_MS = 20;

/**
 * Establishes an SSR request context for the async subtree.
 * Does NOT log anything itself — logging is done by logSsrFetch inside each fetch call.
 *
 * Options:
 *   onlyWithContext — skip execution entirely if no SSR context exists (e.g. Server Action calls).
 */
export const withServerTiming = async <T>(
  label: string,
  fn: () => Promise<T>,
  options?: { onlyWithContext?: boolean },
): Promise<T> => {
  if (options?.onlyWithContext && !getSsrContext()) {
    return fn();
  }

  // If no context yet — this is the top-level SSR call; create a new request context.
  if (!getSsrContext()) {
    return runWithSsrContext(label, fn);
  }
  return fn();
};

/**
 * Next.js Data Cache (`fetch` + `next.revalidate`) does not expose hit/miss reliably.
 * Prefer response cache headers when present; otherwise infer from duration.
 */
export const resolveFetchCacheStatus = (ms: number, response?: Response): string => {
  const header =
    response?.headers.get('x-nextjs-cache') ??
    response?.headers.get('x-vercel-cache') ??
    response?.headers.get('cf-cache-status');

  if (header) return header.toLowerCase();
  return ms < 25 ? 'likely-hit' : 'likely-miss';
};

// ---------------------------------------------------------------------------
// Cache timing helpers — kept for unstable_cache wrappers
// ---------------------------------------------------------------------------

type TimingMeta = Record<string, string | number | boolean | null | undefined>;

/** @internal Used by withCacheTiming / logCacheMiss only. */
const logCacheEvent = (label: string, ms: number, meta: TimingMeta) => {
  const ctx = getSsrContext();
  if (!ctx) return;

  const parts = Object.entries(meta)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `[${k}=${v}]`);

  console.info(
    `[${ctx.pageId}] [${ctx.page}] ${label} timing=[${Math.round(ms)}ms]${parts.length ? ` ${parts.join(' ')}` : ''}`,
  );
};

/** Log inside an `unstable_cache` impl — runs only on cache miss. */
export const logCacheMiss = (label: string, startedAt: number, meta?: TimingMeta) => {
  logCacheEvent(label, performance.now() - startedAt, { ...meta, cache: 'miss' });
};

/**
 * Wraps a cached function. Hits are logged here (fast path).
 * Misses must call `logCacheMiss` inside the unstable_cache implementation.
 * No Node-only APIs (safe for the module graph; import only from server code).
 */
export const withCacheTiming = <TArgs extends unknown[], TResult>(
  label: string,
  cachedFn: (...args: TArgs) => Promise<TResult>,
  metaFromArgs?: (...args: TArgs) => TimingMeta,
): ((...args: TArgs) => Promise<TResult>) => {
  return async (...args: TArgs) => {
    const start = performance.now();
    const result = await cachedFn(...args);
    const ms = performance.now() - start;

    // Miss path already logged from the impl (slow). Hit path skips the impl.
    if (ms < CACHE_HIT_MAX_MS) {
      logCacheEvent(label, ms, { ...(metaFromArgs?.(...args) ?? {}), cache: 'hit' });
    }

    return result;
  };
};
